import React, { useEffect, useState } from 'react';
import { BuildingIcon, UserIcon } from 'lucide-react';
import { CustomerContact, CustomerType, ProCustomer } from '../../types/pro';
import { toISODate } from '../../utils/pro';
import { contactsOf, normalizeContacts } from '../../utils/contacts';
import { Modal } from './Modal';
import { ContactsEditor } from './ContactsEditor';
interface CustomerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Omit to create a new customer. */
  customer?: ProCustomer;
  /** Suggested number for a new customer. */
  nextCustomerNo: string;
  onSave: (customer: Omit<ProCustomer, 'id'>) => void;
}
interface Draft {
  type: CustomerType;
  customerNo: string;
  name: string;
  contacts: CustomerContact[];
  cvr: string;
  address: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
  notes: string;
}
const blank = (customerNo: string): Draft => ({
  type: 'privat',
  customerNo,
  name: '',
  contacts: [],
  cvr: '',
  address: '',
  postalCode: '',
  city: '',
  phone: '',
  email: '',
  notes: ''
});

/**
 * One form for both kinds of customer. A private person and a company differ
 * only in whether a contact person and CVR apply, so the type is a switch at
 * the top rather than two separate forms to keep apart.
 */
export function CustomerFormModal({
  isOpen,
  onClose,
  customer,
  nextCustomerNo,
  onSave
}: CustomerFormModalProps) {
  const [draft, setDraft] = useState<Draft>(blank(nextCustomerNo));
  useEffect(() => {
    if (!isOpen) return;
    setDraft(customer ? {
      type: customer.type ?? 'privat',
      customerNo: customer.customerNo,
      name: customer.name,
      contacts: contactsOf(customer),
      cvr: customer.cvr ?? '',
      address: customer.address,
      postalCode: customer.postalCode,
      city: customer.city,
      phone: customer.phone,
      email: customer.email,
      notes: customer.notes
    } : blank(nextCustomerNo));
  }, [isOpen, customer, nextCustomerNo]);
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => setDraft(current => ({
    ...current,
    [key]: value
  }));
  const isCompany = draft.type === 'firma';
  const canSave = draft.name.trim().length > 0;
  const submit = () => {
    if (!canSave) return;
    const contacts = normalizeContacts(draft.contacts);
    const lead = contacts.find(contact => contact.primary);
    onSave({
      customerNo: draft.customerNo.trim() || nextCustomerNo,
      type: draft.type,
      name: draft.name.trim(),
      contacts,
      // Kept in step so anything still reading the old field stays correct.
      contactPerson: lead?.name ?? '',
      cvr: isCompany ? draft.cvr.trim() : '',
      address: draft.address.trim(),
      postalCode: draft.postalCode.trim(),
      city: draft.city.trim(),
      phone: draft.phone.trim(),
      email: draft.email.trim(),
      notes: draft.notes.trim(),
      createdDate: customer?.createdDate ?? toISODate(new Date()),
      leadSource: customer?.leadSource
    });
    onClose();
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const label = 'mb-1.5 block text-sm font-medium text-stone-700';
  const types: {
    id: CustomerType;
    label: string;
    icon: typeof UserIcon;
  }[] = [{
    id: 'privat',
    label: 'Privatperson',
    icon: UserIcon
  }, {
    id: 'firma',
    label: 'Firma',
    icon: BuildingIcon
  }];
  return <Modal isOpen={isOpen} onClose={onClose} title={customer ? 'Rediger kunde' : 'Ny kunde'} subtitle={customer ? `${customer.customerNo} · oprettet ${customer.createdDate}` : 'Vælg om det er en privatperson eller et firma'} footer={<>
          <button onClick={onClose} className="rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100" data-id="element-118" data-mp="3fh3hzt6vcrov">
            Annuller
          </button>
          <button onClick={submit} disabled={!canSave} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400" data-id="element-119" data-mp="180oeunkvttcq">
            {customer ? 'Gem ændringer' : 'Opret kunde'}
          </button>
        </>} data-id="element-117" data-mp="zslf7rfccr8n">
      <div className="space-y-4" data-id="element-120" data-mp="1qyh3qlyo82r4">
        <fieldset data-id="element-121" data-mp="1qxftgjphbkag">
          <legend className={label} data-id="element-122" data-mp="1qxz2tc12d0cv">Kundetype</legend>
          <div className="grid grid-cols-2 gap-2" data-id="element-123" data-mp="1c1glra6a7m5u">
            {types.map(option => {
            const Icon = option.icon;
            const active = draft.type === option.id;
            return <button key={option.id} type="button" onClick={() => set('type', option.id)} aria-pressed={active} className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-colors ${active ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-124" data-mp="ljlipsztftb">
                  <Icon className="h-4 w-4" data-id="element-125" data-mp="2osrwfwwgz8f6" />
                  {option.label}
                </button>;
          })}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-126" data-mp="1w4481sgfc2xe">
          <label className="block sm:col-span-2" data-id="element-127" data-mp="29upq5ey208u8">
            <span className={label} data-id="element-128" data-mp="29vj0sr04vihn">
              {isCompany ? 'Firmanavn' : 'Navn'}
              <span className="text-red-500" data-id="element-129" data-mp="252yahzgfrniv"> *</span>
            </span>
            <input autoFocus value={draft.name} onChange={e => set('name', e.target.value)} placeholder={isCompany ? 'f.eks. Bygma Auning A/S' : 'f.eks. Jens Hansen'} className={field} data-id="element-130" data-mp="1he6ppt56e7g4" />
          </label>

          {isCompany && <label className="block sm:col-span-2" data-id="element-131" data-mp="3hfwf1sb2t7hc">
              <span className={label} data-id="element-132" data-mp="3helgc6whmpx3">CVR-nummer</span>
              <input value={draft.cvr} onChange={e => set('cvr', e.target.value)} placeholder="8 cifre" inputMode="numeric" className={field} data-id="element-133" data-mp="3m8fvcx15egpu" />
            </label>}

          <label className="block sm:col-span-2" data-id="element-134" data-mp="2yhk3y10ltnj4">
            <span className={label} data-id="element-135" data-mp="2yibzxcy45n9x">Adresse</span>
            <input value={draft.address} onChange={e => set('address', e.target.value)} placeholder="Vej og nummer" className={field} data-id="element-136" data-mp="33h6m8ovahtys" />
          </label>
          <label className="block" data-id="element-137" data-mp="ipmkirzr85zd">
            <span className={label} data-id="element-138" data-mp="iss9m78dv7v3">Postnummer</span>
            <input value={draft.postalCode} onChange={e => set('postalCode', e.target.value)} inputMode="numeric" className={field} data-id="element-139" data-mp="nozgc28lsne3" />
          </label>
          <label className="block" data-id="element-140" data-mp="23a1elmhkfvau">
            <span className={label} data-id="element-141" data-mp="23b2srztvoxws">By</span>
            <input value={draft.city} onChange={e => set('city', e.target.value)} className={field} data-id="element-142" data-mp="1ygyj38t8abs0" />
          </label>
          <label className="block" data-id="element-143" data-mp="1ojki8hjsfvkt">
            <span className={label} data-id="element-144" data-mp="1oklq6d5ic1yb">Telefon</span>
            <input value={draft.phone} onChange={e => set('phone', e.target.value)} inputMode="tel" className={field} data-id="element-145" data-mp="1ki8n5wi5ai58" />
          </label>
          <label className="block" data-id="element-146" data-mp="1akb0vejuivf9">
            <span className={label} data-id="element-147" data-mp="1alcmue2eh2br">E-mail</span>
            <input value={draft.email} onChange={e => set('email', e.target.value)} inputMode="email" className={field} data-id="element-148" data-mp="39x5b9mb4gzcv" />
          </label>
          <label className="block sm:col-span-2" data-id="element-149" data-mp="2dszvs19tepkx">
            <span className={label} data-id="element-150" data-mp="2dt873oqahmu0">Noter</span>
            <textarea value={draft.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Adgangsforhold, aftaler, særlige hensyn…" className={`${field} leading-relaxed`} data-id="element-151" data-mp="2irjjo77yybfx" />
          </label>
          <label className="block" data-id="element-152" data-mp="1v28emdjl5uvz">
            <span className={label} data-id="element-153" data-mp="1v170g079wsa1">Kundenummer</span>
            <input value={draft.customerNo} onChange={e => set('customerNo', e.target.value)} className={`${field} font-mono`} data-id="element-154" data-mp="201bk1z3ha34h" />
          </label>
        </div>

        <div className="border-t border-stone-200 pt-4" data-id="element-155" data-mp="1q2vptn95mdqr">
          <ContactsEditor contacts={draft.contacts} onChange={contacts => set('contacts', contacts)} emptyHint={isCompany ? 'Tilføj de personer du taler med i firmaet' : 'Tilføj flere personer, f.eks. en ægtefælle eller en lejer'} data-id="element-156" data-mp="1q3dw7cpj68ce" />
        </div>
      </div>
    </Modal>;
}